import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import { ElasticStateA, ElasticStateB, ElasticParams, DataCell } from './types';
import { systemDeriv } from './deriv';
import { samplePerturb } from '../shared/hash';
import { NbParams } from '../shared/neighbor';
import type { NeighborPipelinesResult } from '../shared/DualStateSystem';

const TWO_PI = 6.28318530718;
const Rk4Result = d.struct({
  th1: d.f32, om1: d.f32, r1: d.f32, dr1: d.f32,
  th2: d.f32, om2: d.f32, r2: d.f32, dr2: d.f32,
});

export interface ElasticNeighborOpts {
  root: TgpuRoot;
  StateArrayA: WgslArray<typeof ElasticStateA>;
  StateArrayB: WgslArray<typeof ElasticStateB>;
  DataArray: WgslArray<typeof DataCell>;
  NbStackArrayA: WgslArray<typeof ElasticStateA>;
  NbStackArrayB: WgslArray<typeof ElasticStateB>;
  paramsBuffer: TgpuBuffer<typeof ElasticParams> & UniformFlag;
  nbParamsBuffer: TgpuBuffer<typeof NbParams> & UniformFlag;
  baseAa: TgpuBuffer<WgslArray<typeof ElasticStateA>> & StorageFlag;
  baseAb: TgpuBuffer<WgslArray<typeof ElasticStateA>> & StorageFlag;
  baseBa: TgpuBuffer<WgslArray<typeof ElasticStateB>> & StorageFlag;
  baseBb: TgpuBuffer<WgslArray<typeof ElasticStateB>> & StorageFlag;
  nbPertAa: TgpuBuffer<WgslArray<typeof ElasticStateA>> & StorageFlag;
  nbPertAb: TgpuBuffer<WgslArray<typeof ElasticStateA>> & StorageFlag;
  nbPertBa: TgpuBuffer<WgslArray<typeof ElasticStateB>> & StorageFlag;
  nbPertBb: TgpuBuffer<WgslArray<typeof ElasticStateB>> & StorageFlag;
  nbDataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
}

export function buildElasticNeighborPipelines(o: ElasticNeighborOpts): NeighborPipelinesResult {
  const root = o.root;

  const initLayout = tgpu.bindGroupLayout({
    baseA: { storage: o.StateArrayA, access: 'mutable' },
    baseB: { storage: o.StateArrayB, access: 'mutable' },
    pertA: { storage: o.NbStackArrayA, access: 'mutable' },
    pertB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: ElasticParams },
    nb: { uniform: NbParams },
  });
  const stepLayout = tgpu.bindGroupLayout({
    baseCurA: { storage: o.StateArrayA }, baseCurB: { storage: o.StateArrayB },
    baseNextA: { storage: o.StateArrayA, access: 'mutable' }, baseNextB: { storage: o.StateArrayB, access: 'mutable' },
    pertCurA: { storage: o.NbStackArrayA }, pertCurB: { storage: o.NbStackArrayB },
    pertNextA: { storage: o.NbStackArrayA, access: 'mutable' }, pertNextB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: ElasticParams },
    nb: { uniform: NbParams },
  });
  const il = initLayout; const sl = stepLayout;

  const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

  const integrate = (th1: number, om1: number, r1: number, dr1: number, th2: number, om2: number, r2: number, dr2: number, m1: number, m2: number, L1: number, L2: number, k1: number, k2: number, dt: number) => {
    'use gpu';
    const hdt = 0.5 * dt;
    const e1 = systemDeriv(th1, om1, r1, dr1, th2, om2, r2, dr2, m1, m2, L1, L2, k1, k2);
    const e2 = systemDeriv(th1 + hdt * e1.da_th, om1 + hdt * e1.da_om, r1 + hdt * e1.da_r, dr1 + hdt * e1.da_dr, th2 + hdt * e1.db_th, om2 + hdt * e1.db_om, r2 + hdt * e1.db_r, dr2 + hdt * e1.db_dr, m1, m2, L1, L2, k1, k2);
    const e3 = systemDeriv(th1 + hdt * e2.da_th, om1 + hdt * e2.da_om, r1 + hdt * e2.da_r, dr1 + hdt * e2.da_dr, th2 + hdt * e2.db_th, om2 + hdt * e2.db_om, r2 + hdt * e2.db_r, dr2 + hdt * e2.db_dr, m1, m2, L1, L2, k1, k2);
    const e4 = systemDeriv(th1 + dt * e3.da_th, om1 + dt * e3.da_om, r1 + dt * e3.da_r, dr1 + dt * e3.da_dr, th2 + dt * e3.db_th, om2 + dt * e3.db_om, r2 + dt * e3.db_r, dr2 + dt * e3.db_dr, m1, m2, L1, L2, k1, k2);
    const s6 = dt / 6.0;
    return Rk4Result({
      th1: th1 + s6 * (e1.da_th + 2 * e2.da_th + 2 * e3.da_th + e4.da_th),
      om1: om1 + s6 * (e1.da_om + 2 * e2.da_om + 2 * e3.da_om + e4.da_om),
      r1: r1 + s6 * (e1.da_r + 2 * e2.da_r + 2 * e3.da_r + e4.da_r),
      dr1: dr1 + s6 * (e1.da_dr + 2 * e2.da_dr + 2 * e3.da_dr + e4.da_dr),
      th2: th2 + s6 * (e1.db_th + 2 * e2.db_th + 2 * e3.db_th + e4.db_th),
      om2: om2 + s6 * (e1.db_om + 2 * e2.db_om + 2 * e3.db_om + e4.db_om),
      r2: r2 + s6 * (e1.db_r + 2 * e2.db_r + 2 * e3.db_r + e4.db_r),
      dr2: dr2 + s6 * (e1.db_dr + 2 * e2.db_dr + 2 * e3.db_dr + e4.db_dr),
    });
  };



  const nbInitCell = (ci: number) => {
    'use gpu';
    const p = il.$.params; const np = il.$.nb;
    const res = p.resolution;
    const x = ci % d.u32(res); const y = ci / d.u32(res);
    const u = d.f32(x) / res; const v = d.f32(y) / res;
    const omu = 1 - u; const omv = 1 - v;
    const th1 = omu * omv * p.cA00_th + u * omv * p.cA10_th + omu * v * p.cA01_th + u * v * p.cA11_th;
    const om1 = omu * omv * p.cA00_om + u * omv * p.cA10_om + omu * v * p.cA01_om + u * v * p.cA11_om;
    const r1 = omu * omv * p.cA00_r + u * omv * p.cA10_r + omu * v * p.cA01_r + u * v * p.cA11_r;
    const dr1 = omu * omv * p.cA00_dr + u * omv * p.cA10_dr + omu * v * p.cA01_dr + u * v * p.cA11_dr;
    const th2 = omu * omv * p.cB00_th + u * omv * p.cB10_th + omu * v * p.cB01_th + u * v * p.cB11_th;
    const om2 = omu * omv * p.cB00_om + u * omv * p.cB10_om + omu * v * p.cB01_om + u * v * p.cB11_om;
    const r2 = omu * omv * p.cB00_r + u * omv * p.cB10_r + omu * v * p.cB01_r + u * v * p.cB11_r;
    const dr2 = omu * omv * p.cB00_dr + u * omv * p.cB10_dr + omu * v * p.cB01_dr + u * v * p.cB11_dr;
    il.$.baseA[ci] = ElasticStateA({ theta1: th1, omega1: om1, stretch1: r1, stretchRate1: dr1 });
    il.$.baseB[ci] = ElasticStateB({ theta2: th2, omega2: om2, stretch2: r2, stretchRate2: dr2 });
    const K = d.u32(np.K); const cc = d.u32(res) * d.u32(res);
    const px = d.f32(x) / res; const py = d.f32(y) / res;
    for (let k = 0; k < K; k++) {
      const kf = d.f32(k);
      const h1 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const idx = d.u32(k) * cc + ci;
      il.$.pertA[idx] = ElasticStateA({ theta1: th1 + h1, omega1: om1, stretch1: r1, stretchRate1: dr1 });
      il.$.pertB[idx] = ElasticStateB({ theta2: th2 + h2, omega2: om2, stretch2: r2, stretchRate2: dr2 });
    }
    il.$.data[ci] = DataCell({ r: 0, g: 0, b: 0, a: 0 });
  };

  const nbStepCell = (ci: number) => {
    'use gpu';
    const p = sl.$.params; const np = sl.$.nb; const dt = p.dt;
    const res = p.resolution; const cc = d.u32(res) * d.u32(res);
    const bA = sl.$.baseCurA[ci]; const bB = sl.$.baseCurB[ci];
    const bn = integrate(bA.theta1, bA.omega1, bA.stretch1, bA.stretchRate1, bB.theta2, bB.omega2, bB.stretch2, bB.stretchRate2, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2, dt);
    sl.$.baseNextA[ci] = ElasticStateA({ theta1: bn.th1, omega1: bn.om1, stretch1: bn.r1, stretchRate1: bn.dr1 });
    sl.$.baseNextB[ci] = ElasticStateB({ theta2: bn.th2, omega2: bn.om2, stretch2: bn.r2, stretchRate2: bn.dr2 });
    const mode = np.nbMode;
    const K = d.u32(np.K);
    const px = d.f32(ci % d.u32(res)) / res; const py = d.f32(ci / d.u32(res)) / res;
    if (mode < 1.5) {
      let best = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta1, pA.omega1, pA.stretch1, pA.stretchRate1, pB.theta2, pB.omega2, pB.stretch2, pB.stretchRate2, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2, dt);
        sl.$.pertNextA[idx] = ElasticStateA({ theta1: pn.th1, omega1: pn.om1, stretch1: pn.r1, stretchRate1: pn.dr1 });
        sl.$.pertNextB[idx] = ElasticStateB({ theta2: pn.th2, omega2: pn.om2, stretch2: pn.r2, stretchRate2: pn.dr2 });
        const dd1 = circDiff(pn.th1 - bn.th1); const dd2 = pn.om1 - bn.om1; const dd3 = pn.r1 - bn.r1; const dd4 = pn.dr1 - bn.dr1;
        const dd5 = circDiff(pn.th2 - bn.th2); const dd6 = pn.om2 - bn.om2; const dd7 = pn.r2 - bn.r2; const dd8 = pn.dr2 - bn.dr2;
        const dist = std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4 + dd5 * dd5 + dd6 * dd6 + dd7 * dd7 + dd8 * dd8);
        if (dist > best) best = dist;
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: best, a: 1 });
    } else if (mode < 2.5) {
      let acc = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta1, pA.omega1, pA.stretch1, pA.stretchRate1, pB.theta2, pB.omega2, pB.stretch2, pB.stretchRate2, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2, dt);
        sl.$.pertNextA[idx] = ElasticStateA({ theta1: pn.th1, omega1: pn.om1, stretch1: pn.r1, stretchRate1: pn.dr1 });
        sl.$.pertNextB[idx] = ElasticStateB({ theta2: pn.th2, omega2: pn.om2, stretch2: pn.r2, stretchRate2: pn.dr2 });
        const dd1 = circDiff(pn.th1 - bn.th1); const dd2 = pn.om1 - bn.om1; const dd3 = pn.r1 - bn.r1; const dd4 = pn.dr1 - bn.dr1;
        const dd5 = circDiff(pn.th2 - bn.th2); const dd6 = pn.om2 - bn.om2; const dd7 = pn.r2 - bn.r2; const dd8 = pn.dr2 - bn.dr2;
        acc = acc + std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4 + dd5 * dd5 + dd6 * dd6 + dd7 * dd7 + dd8 * dd8);
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: acc, a: 1 });
    } else {
      const sigma = np.perturb;
      let sumLog = 0.0;
      for (let k = 0; k < K; k++) {
        const kf = d.f32(k);
        const h1 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const pn = integrate(bA.theta1 + h1, bA.omega1, bA.stretch1, bA.stretchRate1, bB.theta2 + h2, bB.omega2, bB.stretch2, bB.stretchRate2, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2, dt);
        const dd1 = circDiff(pn.th1 - bn.th1); const dd2 = pn.om1 - bn.om1; const dd3 = pn.r1 - bn.r1; const dd4 = pn.dr1 - bn.dr1;
        const dd5 = circDiff(pn.th2 - bn.th2); const dd6 = pn.om2 - bn.om2; const dd7 = pn.r2 - bn.r2; const dd8 = pn.dr2 - bn.dr2;
        sumLog = sumLog + std.log(std.max(std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4 + dd5 * dd5 + dd6 * dd6 + dd7 * dd7 + dd8 * dd8), 1e-12) / sigma);
      }
      const gmLog = sumLog / np.K;
      const data = sl.$.data[ci];
      let count = data.a; let sumv = data.b;
      if (count < 0.5) { count = 1; sumv = gmLog; } else { count = count + 1; sumv = sumv + gmLog; }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: (sumv / count) / dt, a: count });
    }
  };

  return {
    nbInitPipeline: root.createGuardedComputePipeline(nbInitCell),
    nbStepPipeline: root.createGuardedComputePipeline(nbStepCell),
    nbInitBG: root.createBindGroup(initLayout, {
      baseA: o.baseAa, baseB: o.baseBa, pertA: o.nbPertAa, pertB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepFwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAa, baseCurB: o.baseBa, baseNextA: o.baseAb, baseNextB: o.baseBb,
      pertCurA: o.nbPertAa, pertCurB: o.nbPertBa, pertNextA: o.nbPertAb, pertNextB: o.nbPertBb,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepBwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAb, baseCurB: o.baseBb, baseNextA: o.baseAa, baseNextB: o.baseBa,
      pertCurA: o.nbPertAb, pertCurB: o.nbPertBb, pertNextA: o.nbPertAa, pertNextB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
  };
}